/*
    © Copyright LinkHMS B.V. All rights reserved.
    * Proprietary and confidential.
    * Unauthorized copying of this file, via any medium is strictly prohibited.
*/
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { AnalyticsAppType } from '@core/enums/analytics.enum';
import { AnalyticsService } from '@core/services/analytics.service';
import { AnalyticsTrackingService } from '@core/services/analytics-tracking.service';
import { GoogleTagManagerService } from '@core/services/google-tag-manager.service';
import { I18nService } from '@core/services/i18n.service';
import { MixpanelService } from '@core/services/mixpanel.service';
import { getRouteTemplate } from '@core/utils/get-route-template';
import { environment } from '@environments/environment';
import moment from 'moment/moment';
import { filter } from 'rxjs';

@Component({
  selector: 'hms-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly DEFAULT_FONT_SIZE = 16;
  private static readonly FULL_HD_WIDTH = 1920;

  analyticsService = inject(AnalyticsService);
  router = inject(Router);
  i18nService = inject(I18nService);
  private readonly gtmService = inject(GoogleTagManagerService);
  private readonly mixpanelService = inject(MixpanelService);
  private readonly analyticsTrackingService = inject(AnalyticsTrackingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly resizeHandler = (): void => {
    this.onResizeListener({
      defaultFontSize: AppComponent.DEFAULT_FONT_SIZE,
      fullHdWidth: AppComponent.FULL_HD_WIDTH,
      html: document.documentElement,
    });
  };

  constructor() {
    this.i18nService.checkDefaultLocales();
    this.router.initialNavigation();
    moment.updateLocale('en', {
      week: {
        dow: 1, // Monday = first day of the week
        doy: 4, // First week of year must contain Jan 4th (ISO standard)
      },
    });

    this.router.events
      .pipe(filter((event): event is RoutesRecognized => event instanceof RoutesRecognized))
      .subscribe((event) => {
        if (event.urlAfterRedirects.includes('/404') && !event.url.includes('/404')) {
          console.warn('Redirected to 404 from:', event.url);
        }
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.analyticsTrackingService.setPage(
          getRouteTemplate(this.router.routerState.snapshot.root),
        );
      });
  }

  ngOnInit(): void {
    this.changeFavicon();
    this.onWindowResize();
    this.gtmService.loadGTM();
    this.mixpanelService.init({
      token: environment.mixpanelToken,
      enabled: environment.analyticsEnabled,
      apiHost: environment.mixpanelApiHost,
    });
    this.analyticsTrackingService.setContext({ app_type: AnalyticsAppType.identity });
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('load', this.resizeHandler);
  }

  onWindowResize(): void {
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('load', this.resizeHandler);
    this.resizeHandler();
  }

  onResizeListener(ars: { fullHdWidth: number; defaultFontSize: number; html: HTMLElement }): void {
    const { fullHdWidth, defaultFontSize, html } = ars;
    let baseFontSize = (window.innerWidth / fullHdWidth) * defaultFontSize;
    if (baseFontSize < 14) {
      baseFontSize = 14;
    }
    html.style.setProperty('--dynamic-font-size', `${baseFontSize.toString()}px`);
  }

  private changeFavicon(): void {
    const favicon = document.getElementById('favicon') as HTMLLinkElement | null;
    const isDevelopment =
      environment.environment === 'local' ||
      environment.environment === 'development' ||
      environment.environment === 'staging';

    if (favicon && isDevelopment) favicon.href = '/assets/icons/logo_dev.ico';
  }
}
